import { useState, useRef, useEffect } from 'react';
import { dgiiApi } from '../../api/dgii';
import { useDgiiCarga } from '../../contexts/DgiiCargaContext';
import { DocumentArrowUpIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import PendingInvoicesSection from './PendingInvoicesSection';

export default function BulkInvoiceUploader({ onUploadComplete, empresaSeleccionada, disabled }) {
	const { colaTimestampInicio, setColaTimestampInicio } = useDgiiCarga();
	const tipoFormulario = '606'; // Solo trabajamos con formulario 606
	const [files, setFiles] = useState([]);
	const [filePreviews, setFilePreviews] = useState({}); // Objeto con id del archivo como key y preview como value
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const [timestampInicioSesion, setTimestampInicioSesion] = useState(null);
	const fileInputRef = useRef(null);
	const dropZoneRef = useRef(null);

	// Restaurar timestamp de cola al volver de otra ruta para que se muestren las cards de estado
	useEffect(() => {
		if (colaTimestampInicio != null && timestampInicioSesion == null) {
			setTimestampInicioSesion(colaTimestampInicio);
		}
	}, [colaTimestampInicio]);
	
	// Deshabilitar carga si no hay empresa seleccionada
	const empresaDeshabilitada = !empresaSeleccionada;

	const handleFileSelect = (selectedFiles) => {
		// Prevenir selección si no hay empresa
		if (empresaDeshabilitada) {
			setError('Debes seleccionar una empresa antes de cargar archivos');
			return;
		}
		
		const newFiles = Array.from(selectedFiles);
		setFiles(prev => [...prev, ...newFiles]);
		setError(null);
		
		// Crear previews para las imágenes
		newFiles.forEach((file) => {
			if (file.type.startsWith('image/')) {
				const reader = new FileReader();
				reader.onloadend = () => {
					setFilePreviews(prev => ({
						...prev,
						[file.name]: reader.result
					}));
				};
				reader.readAsDataURL(file);
			}
		});
	};

	const handleInputChange = (e) => {
		if (e.target.files && e.target.files.length > 0) {
			handleFileSelect(e.target.files);
		}
	};

	const handleDragOver = (e) => {
		if (empresaDeshabilitada) {
			return; // No permitir drag si no hay empresa
		}
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e) => {
		if (empresaDeshabilitada) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDrop = (e) => {
		if (empresaDeshabilitada) {
			e.preventDefault();
			e.stopPropagation();
			setError('Debes seleccionar una empresa antes de cargar archivos');
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			handleFileSelect(e.dataTransfer.files);
		}
	};

	const removeFile = (index) => {
		const fileToRemove = files[index];
		setFiles(prev => prev.filter((_, i) => i !== index));
		// Eliminar preview si existe
		if (fileToRemove && filePreviews[fileToRemove.name]) {
			setFilePreviews(prev => {
				const newPreviews = { ...prev };
				delete newPreviews[fileToRemove.name];
				return newPreviews;
			});
		}
	};

	const handleUpload = async () => {
		if (files.length === 0) {
			setError('Por favor selecciona al menos un archivo');
			return;
		}

		setUploading(true);
		setError(null);

		if (!empresaSeleccionada) {
			setError('Por favor selecciona una empresa antes de subir las facturas');
			setUploading(false);
			return;
		}

		// Cada carga debe tener su propio timestamp de inicio (estadísticas por carga)
		const ts = Date.now() / 1000; // Timestamp en segundos
		setTimestampInicioSesion(ts);
		setColaTimestampInicio(ts); // Persistir en context para que las cards sigan visibles al cambiar menú

		try {
			// Subir archivos sin formulario - se crearán automáticamente durante el procesamiento
			const uploadPromises = files.map(file => {
				const formData = new FormData();
				formData.append('archivo', file);
				formData.append('nombre_original', file.name);
				formData.append('tipo_formulario', tipoFormulario);
				formData.append('tipo_carga', 'masiva'); // Marcar como carga masiva
				// CRÍTICO: Incluir empresa seleccionada como string para FormData
				formData.append('empresa_id', String(empresaSeleccionada));
				// NO incluir método de pago - se tomará de n8n o se marcará para editar
				
				return dgiiApi.uploadFactura(null, formData);
			});
			
			const results = await Promise.all(uploadPromises);
			
			setFiles([]);
			setFilePreviews({});
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
			
			// Llamar al callback después de un pequeño delay para asegurar que el backend procesó
			setTimeout(() => {
				if (onUploadComplete) {
					onUploadComplete();
				}
			}, 500);
		} catch (err) {
			console.error('Error subiendo archivos:', err);
			setError(err.response?.data?.detail || err.message || 'Error al subir facturas');
		} finally {
			setUploading(false);
		}
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
	};

	return (
		<div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6">
			<h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
				Masiva
			</h3>
			
			<div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
				<p className="text-xs sm:text-sm text-blue-800">
					<strong>Nota:</strong> En masiva, cada página del PDF será tratada como una factura separada. 
					El método de pago se tomará de la respuesta de n8n. Si n8n no proporciona el método de pago, 
					la factura aparecerá marcada como prioridad para editar.
				</p>
			</div>
			
			{/* Zona de carga */}
			<div
				ref={dropZoneRef}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				className={`border-2 border-dashed rounded-lg p-4 sm:p-6 md:p-8 text-center transition-all ${
					empresaDeshabilitada
						? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
						: isDragging
						? 'border-blue-500 bg-blue-50'
						: 'border-gray-300 hover:border-blue-400 bg-gray-50 active:bg-gray-100'
				}`}
			>
				<DocumentArrowUpIcon className={`mx-auto h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 mb-3 sm:mb-4 transition-colors ${
					empresaDeshabilitada ? 'text-gray-300' : isDragging ? 'text-blue-500' : 'text-gray-400'
				}`} />
				
				{/* Botón de acción */}
				<div className="flex justify-center items-center mb-3 sm:mb-4">
					<label className={empresaDeshabilitada ? 'cursor-not-allowed' : 'cursor-pointer'}>
						<button
							type="button"
							onClick={() => {
								if (empresaDeshabilitada) {
									setError('Debes seleccionar una empresa antes de cargar archivos');
									return;
								}
								fileInputRef.current?.click();
							}}
							disabled={empresaDeshabilitada}
							className={`inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-lg transition-opacity touch-manipulation shadow-sm ${
								empresaDeshabilitada
									? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
									: 'bg-primary text-white hover:opacity-90 active:opacity-80'
							}`}
						>
							<DocumentArrowUpIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
							<span className="whitespace-nowrap">Seleccionar Archivos</span>
						</button>
						<input
							ref={fileInputRef}
							type="file"
							multiple
							accept=".pdf,.png,.jpg,.jpeg,.tiff,.bmp"
							onChange={handleInputChange}
							disabled={empresaDeshabilitada}
							className="hidden"
						/>
					</label>
				</div>
				
				{empresaDeshabilitada ? (
					<p className="text-xs sm:text-sm text-gray-500 mb-1 italic">
						Selecciona una empresa para habilitar la carga de archivos
					</p>
				) : (
					<>
						<p className="text-xs sm:text-sm text-gray-600 mb-1">
							<span className="hidden sm:inline">También puedes arrastrar y soltar archivos aquí</span>
							<span className="sm:hidden">o toca para seleccionar</span>
						</p>
						<p className="text-xs text-gray-500">
							PDF, PNG, JPG, JPEG, TIFF, BMP
						</p>
						<p className="text-xs text-gray-400 mt-1">
							Múltiples archivos permitidos
						</p>
					</>
				)}
			</div>

			{/* Lista de archivos seleccionados */}
			{files.length > 0 && (
				<div className="mt-4 space-y-2">
					<div className="flex items-center justify-between mb-2">
						<h4 className="font-medium text-gray-700 text-sm sm:text-base">
							Archivos ({files.length})
						</h4>
						{files.length > 0 && (
							<button
								type="button"
								onClick={() => {
									setFiles([]);
									setFilePreviews({});
									if (fileInputRef.current) {
										fileInputRef.current.value = '';
									}
								}}
								className="text-xs text-red-600 hover:text-red-700 font-medium"
							>
								Limpiar todo
							</button>
						)}
					</div>
					<div className="max-h-48 sm:max-h-60 overflow-y-auto space-y-2 pr-1">
						{files.map((file, index) => (
							<div 
								key={index} 
								className="flex items-start space-x-3 bg-gray-50 p-2.5 sm:p-3 rounded-lg border border-gray-200"
							>
								{/* Preview de imagen */}
								{filePreviews[file.name] && (
									<div className="flex-shrink-0">
										<img
											src={filePreviews[file.name]}
											alt={`Preview ${file.name}`}
											className="h-16 w-16 object-cover rounded-lg border border-gray-300"
										/>
									</div>
								)}
								
								{/* Información del archivo */}
								<div className="flex-1 min-w-0">
									<div className="flex items-center justify-between">
										<div className="flex-1 min-w-0 pr-2">
											<p className="text-xs sm:text-sm font-medium text-gray-800 truncate">
												{file.name}
											</p>
											<p className="text-xs text-gray-500 mt-0.5">
												{formatFileSize(file.size)}
											</p>
										</div>
										<button
											type="button"
											onClick={() => removeFile(index)}
											className="ml-2 text-red-600 hover:text-red-700 active:text-red-800 flex-shrink-0 p-1 touch-manipulation"
											title="Eliminar archivo"
										>
											<XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Mensaje de error */}
			{error && (
				<div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm">
					{error}
				</div>
			)}

			{/* Botón de subida */}
			{files.length > 0 && (
				<button
					onClick={handleUpload}
					disabled={uploading}
					className="mt-4 w-full bg-secondary text-white py-3 sm:py-3.5 px-4 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-opacity text-sm sm:text-base shadow-sm touch-manipulation flex items-center justify-center space-x-2"
				>
					{uploading ? (
						<>
							<ClockIcon className="h-5 w-5 animate-spin" />
							<span>Subiendo {files.length} archivo(s)...</span>
						</>
					) : (
						<>
							<DocumentArrowUpIcon className="h-5 w-5" />
							<span>Subir {files.length} archivo(s)</span>
						</>
					)}
				</button>
			)}
			
			{/* Lista de pendientes masivos */}
			<PendingInvoicesSection
				tipoCarga="masiva"
				onProcessComplete={onUploadComplete}
				onRefresh={onUploadComplete}
				empresaId={empresaSeleccionada}
				timestampInicioSesion={timestampInicioSesion}
			/>
		</div>
	);
}

